import React, {useState} from 'react';
import qs from 'query-string';
import {Button, FlexContainer, Heading, Text, TextField} from '@forecast-it/design-system';
import Logo, {LOGO_SIZE} from '../forecast-app/shared/components/logo/Logo';
import {Content, Page, PageContent} from './sage-signup-flow/Foreast.styled';
import {WideButton} from './sage-signup-flow/Sage.styled';
import {ErrorText, Form, FullWidthField} from './sage-signup-flow/McpAuthorize.styled';

const PHASE = {LOGIN: 'login', CONSENT: 'consent'};

function authServerUrl() {
	return process.env.MCP_OAUTH_SERVER_URL || '';
}

function getRequestId() {
	return qs.parse(window.location.search).request_id || '';
}

const ConsentView = ({requestId, user, clientName}) => {
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	const respond = async approved => {
		setError('');
		setBusy(true);
		try {
			const resp = await fetch(`${authServerUrl()}/consent`, {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				credentials: 'omit',
				body: JSON.stringify({request_id: requestId, approved}),
			});
			const data = await resp.json();
			if (!resp.ok || !data.redirect) {
				setError('This authorization link is invalid or has expired.');
				return;
			}
			window.location.assign(data.redirect);
		} catch (e) {
			setError('Something went wrong. Please try again.');
		} finally {
			setBusy(false);
		}
	};

	const who = user ? `${user.firstName} ${user.lastName}`.trim() || user.email : '';
	return (
		<Form>
			<Heading size="3">Authorize access</Heading>
			<Text>
				Grant <b>{clientName}</b> access to your Forecast account
				{who ? ` as ${who}` : ''}
				{user && user.companyName ? ` (${user.companyName})` : ''}.
			</Text>
			{error ? <ErrorText>{error}</ErrorText> : null}
			<WideButton onPress={busy ? undefined : () => respond(true)}>Authorize</WideButton>
			<Button onPress={busy ? undefined : () => respond(false)}>Deny</Button>
		</Form>
	);
};

const McpAuthorize = () => {
	const requestId = getRequestId();
	const [phase, setPhase] = useState(PHASE.LOGIN);
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [user, setUser] = useState(null);
	const [clientName, setClientName] = useState('');
	const [error, setError] = useState('');
	const [busy, setBusy] = useState(false);

	const login = async () => {
		setError('');
		if (!requestId) {
			setError('This authorization link is invalid or has expired.');
			return;
		}
		setBusy(true);
		try {
			const resp = await fetch(`${authServerUrl()}/login`, {
				method: 'POST',
				headers: {'Content-Type': 'application/json'},
				credentials: 'omit',
				body: JSON.stringify({request_id: requestId, email, password}),
			});
			const data = await resp.json();
			if (!resp.ok || !data.ok) {
				setError(
					data.error === 'invalid_request'
						? 'This authorization link is invalid or has expired.'
						: 'Invalid email or password.'
				);
				return;
			}
			setUser(data.user);
			setClientName((data.client && data.client.name) || 'the application');
			setPhase(PHASE.CONSENT);
		} catch (e) {
			setError('Something went wrong. Please try again.');
		} finally {
			setBusy(false);
		}
	};

	return (
		<Page>
			<Content alignItems="center" justifyContent="center">
				<Logo size={LOGO_SIZE.MEDIUM} />
				<FlexContainer>
					<PageContent gap="xl">
						{phase === PHASE.LOGIN && (
							<Form>
								<Heading size="3">Sign in to Forecast</Heading>
								<FullWidthField>
									<TextField placeholder="Email" value={email} onValueChange={setEmail} />
								</FullWidthField>
								<FullWidthField>
									<TextField
										placeholder="Password"
										value={password}
										onValueChange={setPassword}
										isPasswordField
									/>
								</FullWidthField>
								{error ? <ErrorText>{error}</ErrorText> : null}
								<WideButton onPress={busy ? undefined : login}>Log in</WideButton>
							</Form>
						)}
						{phase === PHASE.CONSENT && <ConsentView requestId={requestId} user={user} clientName={clientName} />}
					</PageContent>
				</FlexContainer>
			</Content>
		</Page>
	);
};

export default McpAuthorize;
export {authServerUrl, getRequestId};
